// src/utils/crisisDetect.js
//
// Client-side language checks on outgoing coach messages. Deliberately
// conservative and phrase-based: they exist so the app surfaces real support
// (Talk to Someone + Canadian lines) inline the moment someone says this,
// independent of whatever the model replies. A floor, not a diagnosis.
//
// Two levels:
//   detectCrisisLanguage — suicide / self-harm. Always surfaces the crisis card.
//   detectSupportThemes  — the specific-service themes from Hockey Alberta's
//                          resource list (bullying, sexual violence, violence at
//                          home, substance use, Indigenous support, "where do I
//                          start", immediate danger). Each maps to lines in
//                          data/crisisResources.js. General mental-health words
//                          (stress, worry, anxious) are NOT themes: the coach
//                          conversation and the topic pages already carry those
//                          lines, and a card on every mention of "stress" would
//                          teach members to ignore the card.

const PATTERNS = [
    /\b(kill|hurt|harm)(ing)? myself\b/i,
    /\bsuicid(e|al)\b/i,
    /\bend (it all|my life|everything)\b/i,
    /\b(don'?t|do not|dont) want to (be here|live|be alive|wake up|exist)\b/i,
    /\bwant to die\b(?! (laughing|of (laughter|embarrassment|boredom)))/i,
    /\bwish I (was|were) dead\b/i,
    /\bbetter off (dead|without me)\b/i,
    /\bno (reason|point) (to|in) (live|living|go on|going on)\b/i,
    /\bcan'?t (go on|do this anymore|take it anymore)\b/i,
    /\bself[- ]?harm/i,
    /\bcut(ting)? myself\b/i,
    /\boverdos(e|ing)\b/i,
    // French basics
    /\bme suicider\b/i,
    /\benvie de mourir\b/i,
    /\bme faire du mal\b/i
];

export const detectCrisisLanguage = (text = '') => {
    if (typeof text !== 'string' || !text.trim()) return false;
    return PATTERNS.some(re => re.test(text));
};

const THEME_PATTERNS = {
    danger: [
        /\b(someone|he|she|they) (is|are) (hurting|going to hurt) me\b/i,
        /\bin (immediate )?danger\b/i,
        /\b(not|un)safe right now\b/i,
        /\bseriously (hurt|injured)\b/i
    ],
    bullying: [
        /\bbull(y|ies|ied|ying)\b/i,
        /\bcyber-?bull/i,
        /\bpicked on\b/i,
        /\bname[- ]calling\b/i,
        /\bharass(ed|ing|ment)?\b/i,
        /\bintimidat(ed|ing|ion)\b/i,
        /\b(leaving|leave|left) me out\b/i,
        /\bspreading rumou?rs\b/i,
        /\b(racist|sexist|homophobic) (comments?|jokes?|stuff|things)\b/i
    ],
    sexual_violence: [
        /\bsexual(ly)? (assault|abuse|harass|violence|comment|pressure)/i,
        /\b(unwanted|inappropriate) touch/i,
        /\btouched me\b/i,
        /\bintimate (image|photo|picture|pic)s?\b/i,
        /\bnudes?\b/i,
        /\bpressured (me )?into\b/i,
        /\b(raped?|molest)/i
    ],
    family_violence: [
        /\b(unsafe|not safe|afraid|scared) (at|to go) home\b/i,
        /\b(domestic|family|dating) violence\b/i,
        /\babuse[ds]? at home\b/i,
        /\bsomeone at home (is )?(hurting|hits|hit) me\b/i,
        /\b(dad|mom|mum|father|mother|parents?|step-?(dad|mom|father|mother)|boyfriend|girlfriend|partner) (hits|hit|hurts|hurt|beats|threatens|threatened|chokes|choked) me\b/i
    ],
    substance: [
        /\baddict(ed|ion)\b/i,
        /\bcan'?t stop (drinking|using|vaping|smoking)\b/i,
        /\bdrinking too much\b/i,
        /\b(drug|drinking|vaping) problem\b/i,
        /\bworried about my (drinking|drug use|use|vaping)\b/i,
        /\busing (drugs|weed|coke|pills)\b/i
    ],
    indigenous: [
        /\bfirst nations?\b/i,
        /\bm[ée]tis\b/i,
        /\binuit\b/i,
        /\bindigenous\b/i
    ],
    find_help: [
        /\bwhere (can|do|should) I (get|find|go for) help\b/i,
        /\bdon'?t know (where to (go|start|turn)|who to (talk|ask|tell))\b/i,
        /\bwho (can|should) I talk to\b/i
    ]
};

// Theme ids (keys of THEMES in data/crisisResources.js) matched by the text.
export const detectSupportThemes = (text = '') => {
    if (typeof text !== 'string' || !text.trim()) return [];
    return Object.keys(THEME_PATTERNS).filter(k => THEME_PATTERNS[k].some(re => re.test(text)));
};
